import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Inbox, MessageCircle, RefreshCw, Send, Loader2, CheckCheck,
  Filter, User, Mail, Clock, Paperclip, ChevronRight, Sparkles, Settings,
} from 'lucide-react';
import api from '../api/client';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import AdminFormBuilder from './AdminFormBuilder';

/**
 * AdminHelpCenterPanel — Phase 48 (admin side).
 * -----------------------------------------------------------------
 * Two sub-tabs: "Chat live" and "Ticket". Deliberately compact — the
 * fuller CMS-style form builder (Phase 48C) and per-user timelines
 * will live in a dedicated admin route. Here we focus on reading &
 * replying so support staff can act on incoming traffic right away.
 */
const AdminHelpCenterPanel = () => {
  const [sub, setSub] = useState('chat');

  return (
    <div className="space-y-4" data-testid="admin-help-center">
      <div className="flex flex-wrap gap-2 rounded-xl border border-border bg-card/50 p-2">
        <SubTab active={sub === 'chat'} onClick={() => setSub('chat')} icon={MessageCircle} testId="admin-help-subtab-chat">
          Chat live
        </SubTab>
        <SubTab active={sub === 'tickets'} onClick={() => setSub('tickets')} icon={Inbox} testId="admin-help-subtab-tickets">
          Ticket
        </SubTab>
        <SubTab active={sub === 'forms'} onClick={() => setSub('forms')} icon={Settings} testId="admin-help-subtab-forms">
          Moduli
        </SubTab>
      </div>
      {sub === 'chat' && <ChatSection />}
      {sub === 'tickets' && <TicketsSection />}
      {sub === 'forms' && <AdminFormBuilder />}
    </div>
  );
};

const SubTab = ({ active, onClick, icon: Icon, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition ${
      active ? 'gold-gradient text-black shadow' : 'text-muted-foreground hover:bg-white/5'
    }`}
  >
    <Icon className="h-3.5 w-3.5" />
    {children}
  </button>
);

// ==================================================================
// Chat section
// ==================================================================

const ChatSection = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      const res = await api.get('/admin/help/chat/conversations', { params });
      setConversations(res.data.conversations || []);
      if (!selectedId && res.data.conversations?.length) {
        setSelectedId(res.data.conversations[0].id);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [statusFilter, selectedId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <ChatEmailAlertSettings />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[340px_1fr]">
      {/* Conversation list */}
      <div className="rounded-xl border border-border bg-card/40">
        <div className="flex items-center gap-2 border-b border-border p-3">
          <Filter className="h-3.5 w-3.5 text-muted-foreground" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 flex-1 text-xs" data-testid="admin-help-chat-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte</SelectItem>
              <SelectItem value="open">Aperte</SelectItem>
              <SelectItem value="closed">Chiuse</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" onClick={load} disabled={refreshing} data-testid="admin-help-chat-refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <div className="max-h-[560px] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-4 w-4 animate-spin" /></div>
          ) : conversations.length === 0 ? (
            <p className="p-4 text-xs text-muted-foreground" data-testid="admin-help-chat-empty">
              Nessuna conversazione attiva.
            </p>
          ) : (
            conversations.map((c) => (
              <ConversationRow
                key={c.id}
                conv={c}
                active={c.id === selectedId}
                onClick={() => setSelectedId(c.id)}
              />
            ))
          )}
        </div>
      </div>

      {/* Conversation detail */}
      <div className="rounded-xl border border-border bg-card/40 lg:min-h-[560px]">
        {selectedId ? (
          <ChatDetail conversationId={selectedId} onStatusChange={load} />
        ) : (
          <div className="flex h-full min-h-[300px] items-center justify-center text-xs text-muted-foreground">
            Seleziona una conversazione dalla lista.
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

/** NeoUI — configure the staff email(s) alerted when a visitor writes
 *  in the support chat. One email per new-message burst. */
const ChatEmailAlertSettings = () => {
  const [emailsText, setEmailsText] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');

  useEffect(() => {
    api.get('/admin/help/notification-settings')
      .then((res) => setEmailsText((res.data.emails || []).join(', ')))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const save = async () => {
    setSaving(true);
    setSavedMsg('');
    try {
      const emails = emailsText.split(/[,;\n]/).map((e) => e.trim()).filter(Boolean);
      const res = await api.put('/admin/help/notification-settings', { emails });
      setEmailsText((res.data.emails || []).join(', '));
      setSavedMsg(`Salvato — ${res.data.emails.length} destinatari`);
    } catch (e) {
      setSavedMsg(String(e.response?.data?.detail || 'Errore di salvataggio').split('|').pop());
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card/40 p-3" data-testid="chat-email-alert-settings">
      <div className="mb-2 flex items-center gap-2">
        <Mail className="h-3.5 w-3.5 gold-text" />
        <h4 className="text-xs font-semibold uppercase tracking-wide">Notifiche email nuovi messaggi chat</h4>
      </div>
      <p className="mb-2 text-[11px] text-muted-foreground">
        Quando un utente scrive in chat, viene inviata un'email a questi indirizzi (una o più email separate da virgola).
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={emailsText}
          onChange={(e) => setEmailsText(e.target.value)}
          placeholder="es. supporto@nymia.me, admin@nymia.me"
          className="h-8 flex-1 min-w-[240px] text-xs"
          disabled={!loaded}
          data-testid="chat-alert-emails-input"
        />
        <Button size="sm" onClick={save} disabled={saving || !loaded} className="h-8 gold-gradient text-black" data-testid="chat-alert-emails-save">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Salva'}
        </Button>
        {savedMsg && <span className="text-[11px] text-muted-foreground" data-testid="chat-alert-emails-status">{savedMsg}</span>}
      </div>
    </div>
  );
};

const ConversationRow = ({ conv, active, onClick }) => {
  const label = conv.user_name || conv.user_email || (conv.anon_session_id ? `Anonimo · ${conv.anon_session_id.slice(0, 6)}` : 'Anonimo');
  const time = new Date(conv.last_message_at).toLocaleString([], { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-start gap-2 border-b border-border/50 p-3 text-left transition ${
        active ? 'bg-amber-500/10' : 'hover:bg-white/[0.03]'
      }`}
      data-testid={`admin-help-chat-conv-${conv.id}`}
    >
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/[0.05]">
        {conv.user_id ? <User className="h-3.5 w-3.5" /> : <MessageCircle className="h-3.5 w-3.5" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between">
          <p className="truncate text-[13px] font-medium text-foreground">{label}</p>
          {conv.unread_by_staff && <span className="h-2 w-2 shrink-0 rounded-full bg-red-500" data-testid="chat-unread-dot" />}
        </div>
        <p className="mt-0.5 truncate text-[11.5px] text-muted-foreground">{conv.last_message_preview || '…'}</p>
        <p className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground/60">{time} · {conv.message_count || 0} msg</p>
      </div>
    </button>
  );
};

const ChatDetail = ({ conversationId, onStatusChange }) => {
  const [messages, setMessages] = useState([]);
  const [conv, setConv] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/help/chat/messages/${conversationId}`);
      setMessages(res.data.messages || []);
      setConv(res.data.conversation);
    } catch (_e) { /* ignore */ }
  }, [conversationId]);

  useEffect(() => {
    load();
    const t = window.setInterval(load, 20000);
    return () => window.clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const res = await api.post(`/admin/help/chat/conversations/${conversationId}/messages`, { text: text.trim() });
      setMessages((prev) => [...prev, res.data]);
      setText('');
    } finally { setSending(false); }
  };

  const toggleStatus = async () => {
    if (!conv) return;
    const target = conv.status === 'open' ? 'closed' : 'open';
    await api.patch(`/admin/help/chat/conversations/${conversationId}`, null, { params: { status: target } });
    setConv((c) => (c ? { ...c, status: target } : c));
    onStatusChange?.();
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-border p-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold" data-testid="admin-help-chat-detail-name">
            {conv?.user_name || conv?.user_email || 'Anonimo'}
          </p>
          <p className="truncate text-[11px] text-muted-foreground">
            {conv?.user_email || conv?.anon_session_id?.slice(0, 12) || '—'} · {conv?.status || 'open'}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={toggleStatus} className="text-xs" data-testid="admin-help-chat-toggle-status">
          {conv?.status === 'closed' ? 'Riapri' : 'Chiudi'}
        </Button>
      </div>
      <div ref={listRef} className="flex-1 space-y-2 overflow-y-auto p-3" data-testid="admin-help-chat-detail-messages">
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.author === 'staff' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[75%] rounded-xl px-3 py-1.5 text-[13px] ${
              m.author === 'staff' ? 'bg-amber-500/20 text-amber-50' : 'bg-white/[0.06]'
            }`}>
              <p className="whitespace-pre-wrap break-words">{m.text}</p>
              <p className="mt-0.5 text-right text-[10px] opacity-70">
                {new Date(m.created_at).toLocaleString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}
      </div>
      <form onSubmit={send} className="border-t border-border p-2" data-testid="admin-help-chat-detail-form">
        <div className="flex items-end gap-2">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Rispondi come staff…"
            rows={2}
            className="text-[13px]"
            data-testid="admin-help-chat-input"
          />
          <Button type="submit" disabled={!text.trim() || sending} className="h-9 gold-gradient text-black" data-testid="admin-help-chat-send">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </form>
    </div>
  );
};

// ==================================================================
// Tickets section
// ==================================================================

const STATUS_LABELS = {
  new: 'Nuovo', in_progress: 'Preso in carico', working: 'In lavorazione',
  waiting_user: 'In attesa utente', closed: 'Chiuso',
};

const STATUS_COLORS = {
  new: 'bg-blue-500/20 text-blue-200',
  in_progress: 'bg-amber-500/20 text-amber-200',
  working: 'bg-violet-500/20 text-violet-200',
  waiting_user: 'bg-orange-500/20 text-orange-200',
  closed: 'bg-emerald-500/20 text-emerald-200',
};

const TicketsSection = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [formFilter, setFormFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [forms, setForms] = useState([]);

  useEffect(() => {
    api.get('/help/forms').then((r) => setForms(r.data.forms || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (formFilter !== 'all') params.form_key = formFilter;
      const res = await api.get('/admin/help/tickets', { params });
      setTickets(res.data.tickets || []);
    } finally { setLoading(false); }
  }, [statusFilter, formFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[340px_1fr]">
      {/* Filters + list */}
      <div className="rounded-xl border border-border bg-card/40">
        <div className="space-y-2 border-b border-border p-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 text-xs" data-testid="admin-help-tickets-filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={formFilter} onValueChange={setFormFilter}>
            <SelectTrigger className="h-8 text-xs" data-testid="admin-help-tickets-filter-form">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i moduli</SelectItem>
              {forms.map((f) => (
                <SelectItem key={f.key} value={f.key}>
                  {f.title?.it || f.title?.en || f.key}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="max-h-[560px] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-4 w-4 animate-spin" /></div>
          ) : tickets.length === 0 ? (
            <p className="p-4 text-xs text-muted-foreground" data-testid="admin-help-tickets-empty">
              Nessun ticket con questi filtri.
            </p>
          ) : (
            tickets.map((tk) => (
              <TicketRow
                key={tk.id}
                tk={tk}
                active={tk.id === selectedId}
                onClick={() => setSelectedId(tk.id)}
              />
            ))
          )}
        </div>
      </div>

      {/* Detail */}
      <div className="rounded-xl border border-border bg-card/40 lg:min-h-[560px]">
        {selectedId ? (
          <TicketDetail ticketId={selectedId} onUpdated={load} forms={forms} />
        ) : (
          <div className="flex h-full min-h-[300px] items-center justify-center text-xs text-muted-foreground">
            Seleziona un ticket dalla lista.
          </div>
        )}
      </div>
    </div>
  );
};

const TicketRow = ({ tk, active, onClick }) => {
  const time = new Date(tk.created_at).toLocaleString([], { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  const formTitle = tk.form_title_snapshot?.it || tk.form_title_snapshot?.en || tk.form_key;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-start gap-2 border-b border-border/50 p-3 text-left transition ${
        active ? 'bg-amber-500/10' : 'hover:bg-white/[0.03]'
      }`}
      data-testid={`admin-help-tickets-row-${tk.id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between">
          <p className="truncate text-[12px] font-mono text-amber-200">{tk.ticket_number}</p>
          <span className={`ml-1 shrink-0 rounded px-1.5 py-[1px] text-[10px] font-semibold ${STATUS_COLORS[tk.status] || ''}`}>
            {STATUS_LABELS[tk.status] || tk.status}
          </span>
        </div>
        <p className="mt-1 truncate text-[12.5px] font-medium">{formTitle}</p>
        <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{tk.user_email}</p>
        <p className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground/60">{time}</p>
      </div>
      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-50" />
    </button>
  );
};

const TicketDetail = ({ ticketId, onUpdated, forms }) => {
  const [tk, setTk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/help/tickets/${ticketId}`);
      setTk(res.data);
    } finally { setLoading(false); }
  }, [ticketId]);

  useEffect(() => { load(); }, [load]);

  const form = useMemo(
    () => forms.find((f) => f.key === tk?.form_key),
    [forms, tk],
  );

  const changeStatus = async (newStatus) => {
    if (!tk || newStatus === tk.status) return;
    setUpdating(true);
    try {
      const res = await api.patch(`/admin/help/tickets/${ticketId}`, null, { params: { status: newStatus } });
      setTk(res.data);
      onUpdated?.();
    } finally { setUpdating(false); }
  };

  if (loading || !tk) return <div className="flex h-full items-center justify-center py-8"><Loader2 className="h-4 w-4 animate-spin" /></div>;

  return (
    <div className="flex h-full flex-col" data-testid="admin-help-tickets-detail">
      <div className="border-b border-border p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-mono text-[12px] gold-text" data-testid="admin-help-tickets-detail-number">{tk.ticket_number}</p>
            <p className="mt-0.5 text-sm font-semibold">{tk.form_title_snapshot?.it || tk.form_title_snapshot?.en || tk.form_key}</p>
          </div>
          <Select value={tk.status} onValueChange={changeStatus} disabled={updating}>
            <SelectTrigger className="h-8 w-[180px] text-xs" data-testid="admin-help-tickets-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="mt-2 grid grid-cols-1 gap-1 text-[11.5px] text-muted-foreground sm:grid-cols-2">
          <p className="flex items-center gap-1"><User className="h-3 w-3" /> {tk.user_name || '—'}</p>
          <p className="flex items-center gap-1"><Mail className="h-3 w-3" /> {tk.user_email}</p>
          <p className="flex items-center gap-1"><Clock className="h-3 w-3" /> {new Date(tk.created_at).toLocaleString()}</p>
          <p className="flex items-center gap-1"><Sparkles className="h-3 w-3" /> Destinatario: {tk.target_email}</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3">
        <h4 className="mb-2 text-[11px] uppercase tracking-wider text-muted-foreground">Contenuto richiesta</h4>
        <div className="space-y-2">
          {Object.entries(tk.payload || {}).map(([k, v]) => {
            const fieldDef = form?.fields?.find((f) => f.key === k);
            const label = fieldDef ? (fieldDef.label?.it || fieldDef.label?.en || k) : k;
            return (
              <div key={k} className="rounded-md border border-white/5 bg-white/[0.02] p-2">
                <p className="text-[10.5px] uppercase tracking-wide text-muted-foreground">{label}</p>
                <p className="mt-0.5 whitespace-pre-wrap break-words text-[13px]">{String(v)}</p>
              </div>
            );
          })}
        </div>

        {tk.attachments?.length > 0 && (
          <>
            <h4 className="mb-2 mt-4 text-[11px] uppercase tracking-wider text-muted-foreground">Allegati</h4>
            <div className="space-y-1">
              {tk.attachments.map((a, i) => (
                <div key={i} className="flex items-center gap-2 rounded-md border border-white/5 bg-white/[0.02] p-2 text-[12px]">
                  <Paperclip className="h-3 w-3 text-muted-foreground" />
                  {a.data_base64 ? (
                    <a
                      href={`data:${a.content_type};base64,${a.data_base64}`}
                      download={a.filename}
                      className="truncate gold-text hover:underline"
                      data-testid={`admin-help-tickets-attachment-${i}`}
                    >
                      {a.filename}
                    </a>
                  ) : (
                    <span className="truncate">{a.filename}</span>
                  )}
                  <span className="ml-auto text-[10px] text-muted-foreground">{Math.round((a.size || 0) / 1024)}KB</span>
                </div>
              ))}
            </div>
          </>
        )}

        {tk.status_history?.length > 0 && (
          <>
            <h4 className="mb-2 mt-4 text-[11px] uppercase tracking-wider text-muted-foreground">Storia stato</h4>
            <div className="space-y-1">
              {tk.status_history.map((h, i) => (
                <div key={i} className="flex items-center gap-2 text-[11.5px] text-muted-foreground">
                  <CheckCheck className="h-3 w-3" />
                  <span className={`rounded px-1.5 py-[1px] text-[10px] font-semibold ${STATUS_COLORS[h.status] || ''}`}>
                    {STATUS_LABELS[h.status] || h.status}
                  </span>
                  <span className="text-[10.5px]">{new Date(h.at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminHelpCenterPanel;
